import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import PageHeader from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Save, Zap, RotateCcw, PenSquare, GraduationCap, CheckCheck } from "lucide-react";
import { toast } from "sonner";

/**
 * GradesEditor — dual-mode: 'year-work' or 'final-exam'.
 *
 * year-work: dynamic components (per subject) → sum + save.
 * final-exam: single grade_value column + read-only year-work total column.
 */
export default function GradesEditor({ mode }) {
  const { ctx, meta } = useApp();
  const grades = meta?.grades_by_stage?.[ctx.stage] || [];
  const [grade, setGrade] = useState("");
  const [cls, setCls] = useState("");
  const [subjectId, setSubjectId] = useState("");
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [students, setStudents] = useState([]);
  const [components, setComponents] = useState([]);
  const [values, setValues] = useState({}); // { student_id: { comp_name: v } | value }
  const [ywTotals, setYwTotals] = useState({}); // final-exam only
  const [saving, setSaving] = useState(false);

  const subject = useMemo(() => subjects.find((s) => s.id === subjectId), [subjects, subjectId]);
  const semester = ctx.semester === "end_year" ? 1 : Number(ctx.semester);

  // Load subjects for stage/year/grade/semester
  useEffect(() => {
    if (!grade) { setSubjects([]); return; }
    api.get("/subjects", { params: { stage: ctx.stage, year: ctx.year, grade, semester } })
      .then(({ data }) => {
        // مواد "الاختبار التكويني" و"نواتج التعلم" لا يوجد لها "أعمال سنة"
        // على الإطلاق (نفس قاعدة البرنامج المكتبي) — فلا تظهر في شاشة رصد
        // أعمال السنة، وتظهر فقط في شاشة رصد الاختبار النهائي مع باقي المواد.
        const filtered = mode === "year-work"
          ? data.filter((s) => s.subject_type === "مادة ذات تقييم ختامي")
          : data;
        setSubjects(filtered);
      })
      .catch((e) => toast.error(formatApiError(e)));
  }, [ctx.stage, ctx.year, grade, semester, mode]);

  // Load classes for grade
  useEffect(() => {
    if (!grade) { setClasses([]); return; }
    api.get("/students/classes", { params: { stage: ctx.stage, year: ctx.year, grade } })
      .then(({ data }) => setClasses(data))
      .catch(() => {});
  }, [ctx.stage, ctx.year, grade]);

  // Load students + effective components + existing grades
  useEffect(() => {
    if (!grade || !cls || !subject) { setStudents([]); setComponents([]); return; }
    // "الفصل" يحدِّد فقط قائمة الطلاب المعروضة (classParam). أما جلب
    // الدرجات نفسها فيتم دائمًا بدون فلترة class — لأن حقل "class" داخل
    // مستند الدرجة قد يحمل الفصل القديم للطالب وقت آخر حفظ، ولا يتزامن
    // تلقائيًا عند نقل الطالب لفصل جديد. الربط الصحيح والوحيد بين الدرجة
    // والطالب هو student_id (انظر map[g.student_id] أدناه)، فلا داعي ولا
    // صحة لفلترة الدرجات بـ class أصلًا — طالما القائمة المعروضة نفسها
    // مفلترة صح بالفصل الحالي عبر classParam في استدعاء /students وحده.
    const classParam = cls === "__all__" ? {} : { class_name: cls };
    (async () => {
      try {
        const [{ data: st }, { data: comps }] = await Promise.all([
          api.get("/students", { params: { stage: ctx.stage, year: ctx.year, grade, ...classParam } }),
          api.get("/subject-components", { params: {
            stage: ctx.stage, year: ctx.year, semester,
            subject_name: subject.subject_name, section: subject.section, grade,
          } }),
        ]);
        // ترتيب تصاعدي برقم الجلوس دائمًا، خصوصًا مهم عند اختيار "كل
        // الفصول" حتى يظهر الطلاب مرتّبين بلا اعتبار للفصل نفسه.
        const sorted = [...st].sort((a, b) =>
          (parseInt(a.seat_number, 10) || 0) - (parseInt(b.seat_number, 10) || 0));
        setStudents(sorted);
        setComponents(comps.components || []);

        if (mode === "year-work") {
          const { data: existing } = await api.get("/grades/year-work", { params: {
            stage: ctx.stage, year: ctx.year, grade,
            subject_name: subject.subject_name, semester, section: subject.section,
          } });
          const map = {};
          existing.forEach((g) => { map[g.student_id] = g.additional_data || {}; });
          setValues(map);
        } else {
          // final-exam: single value per student
          const { data: existing } = await api.get("/grades/final-exam", { params: {
            stage: ctx.stage, year: ctx.year, grade,
            subject_name: subject.subject_name, semester, section: subject.section,
          } });
          const map = {};
          existing.forEach((g) => { map[g.student_id] = g.grade_value; });
          setValues(map);
          // also load YW totals for reference (subject-level)
          const { data: yw } = await api.get("/grades/year-work", { params: {
            stage: ctx.stage, year: ctx.year, grade,
            subject_name: subject.subject_name, semester, section: subject.section,
          } });
          const ymap = {};
          yw.forEach((g) => { ymap[g.student_id] = g.grade_value; });
          setYwTotals(ymap);
        }
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [ctx.stage, ctx.year, grade, cls, subjectId, semester, mode]);

  const setYwComp = (sid, cname, v) => {
    setValues((prev) => ({ ...prev, [sid]: { ...(prev[sid] || {}), [cname]: v } }));
  };
  const setFinalVal = (sid, v) => setValues((prev) => ({ ...prev, [sid]: v }));

  // "حساب المتوسط": مكوّنات هذا الطالب المصدر (Set بأسماء) — تُستبعَد من
  // المجموع لأنها تُحتسَب داخل قيمة المتوسط الناتج، لا بجانبه. مطابق تمامًا
  // لنفس منطق الاستبعاد المستخدَم في شاشة القوالب (Templates.jsx)، لكن هنا
  // على القيم المُدخَلة فعليًا لكل طالب، لا على الدرجات العظمى للقالب.
  const averagedSourceNames = new Set(
    components.filter((c) => c.is_average).flatMap((c) => c.source_components || [])
  );

  /** القيمة الحالية لمكوّن "متوسط" لطالب معيّن — متوسط القيم المُدخَلة
   * فعليًا لمكوّناته المصدر (وليس درجاتها العظمى). تُحسَب حيّة مع كل
   * تعديل، ولا تُخزَّن كحقل مستقل — القيمة النهائية فقط هي ما يُحفَظ ضمن
   * grade_value الكلي (عبر rowTotal أدناه)، بلا أي حقل إضافي في قاعدة
   * البيانات. */
  const averageValue = (row, comp) => {
    const sources = comp.source_components || [];
    if (sources.length === 0) return 0;
    const vals = sources.map((n) => parseFloat(row[n]) || 0);
    return vals.reduce((a, b) => a + b, 0) / vals.length;
  };

  const rowTotal = (sid) => {
    const row = values[sid] || {};
    let sum = 0;
    components.forEach((c) => {
      if (averagedSourceNames.has(c.name)) return; // تُحتسَب فقط ضمن متوسطها، لا تُجمَع مرة أخرى مستقلة
      sum += c.is_average ? averageValue(row, c) : (parseFloat(row[c.name]) || 0);
    });
    return sum;
  };

  /** القيمة القصوى الصحيحة لهذه المادة تحديدًا في رصد الاختبار النهائي:
   * نواتج التعلم → "اجتاز" (تقدير نصّي، لا رقم)، الاختبار التكويني →
   * الدرجة العظمى للمادة (لأن لا يوجد أعمال سنة تُخصَم منها)، وباقي
   * المواد → درجة الاختبار النهائي المخصَّصة لها فقط. */
  const finalExamMaxValue = (subj) => {
    if (!subj) return 100;
    if (subj.subject_type === "مادة ذات تقييم وفق نواتج التعلم") return "اجتاز";
    if (subj.subject_type === "مادة ذات اختبار تكويني") return subj.max_score || 100;
    return subj.final_exam_score || 100;
  };

  const fillMax = (sid) => {
    if (mode === "year-work") {
      const patch = {};
      // المتوسط قيمة مشتقّة تلقائيًا (تُحسَب من مصادرها) — لا تُملأ مباشرة؛
      // تعبئة مصادرها بالحد الأقصى تكفي لتصل لأقصى قيمة ممكنة تلقائيًا.
      components.forEach((c) => { if (!c.is_average) patch[c.name] = c.max_score; });
      setValues((prev) => ({ ...prev, [sid]: patch }));
    } else {
      setFinalVal(sid, finalExamMaxValue(subject));
    }
  };
  /** تعبئة كل الطلاب دفعة واحدة بالحد الأقصى — بدل الاضطرار للضغط على
   * زر التعبئة لكل طالب على حدة واحدًا تلو الآخر. */
  const fillMaxAll = () => {
    if (!students.length) return;
    if (mode === "year-work") {
      const patch = {};
      components.forEach((c) => { if (!c.is_average) patch[c.name] = c.max_score; });
      setValues((prev) => {
        const next = { ...prev };
        students.forEach((st) => { next[st.id] = { ...patch }; });
        return next;
      });
    } else {
      const maxV = finalExamMaxValue(subject);
      setValues((prev) => {
        const next = { ...prev };
        students.forEach((st) => { next[st.id] = maxV; });
        return next;
      });
    }
    toast.success(`تم تعبئة الحد الأقصى لجميع الطلاب (${students.length})`);
  };
  const resetRow = (sid) => {
    if (mode === "year-work") setValues((prev) => ({ ...prev, [sid]: {} }));
    else setFinalVal(sid, "");
  };
  const resetAll = () => {
    if (!window.confirm("مسح كل الدرجات المُعروضة (بدون حفظ)؟")) return;
    setValues({});
  };

  const save = async () => {
    if (!subject || students.length === 0) return;
    setSaving(true);
    try {
      if (mode === "year-work") {
        const items = students.map((st) => ({
          stage: ctx.stage, year: ctx.year, grade, class: st.class,
          subject_name: subject.subject_name, section: subject.section, student_id: st.id,
          seat_number: st.seat_number || "", student_name: st.name,
          grade_value: rowTotal(st.id),
          additional_data: values[st.id] || {},
          semester,
        }));
        await api.post("/grades/year-work/bulk", { items });
      } else {
        const items = students.map((st) => {
          const v = values[st.id];
          return {
            stage: ctx.stage, year: ctx.year, grade, class: st.class,
            subject_name: subject.subject_name, section: subject.section, student_id: st.id,
            seat_number: st.seat_number || "", student_name: st.name,
            grade_value: (v === undefined || v === "") ? 0 : v,
            notes: "", semester,
          };
        });
        await api.post("/grades/final-exam/bulk", { items });
      }
      toast.success("تم حفظ الدرجات");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const isOutcome = subject?.subject_type === "مادة ذات تقييم وفق نواتج التعلم";
  const isFormative = subject?.subject_type === "مادة ذات اختبار تكويني";
  const IconTitle = mode === "year-work" ? PenSquare : GraduationCap;

  return (
    <div className="space-y-6" data-testid={`grades-editor-${mode}`}>
      <PageHeader
        icon={IconTitle}
        overline="رصد الدرجات"
        title={mode === "year-work" ? "رصد أعمال السنة" : "رصد الاختبار النهائي"}
        subtitle={mode === "year-work"
          ? "رصد مكوّنات أعمال السنة لكل مادة مع الحساب التلقائي للمجموع."
          : "رصد درجات الاختبار النهائي مع عرض مجموع أعمال السنة كمرجع."}
      />

      <div className="rounded-xl border border-border bg-white p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <SF label="الصف" value={grade} onChange={setGrade} options={grades} testId="ge-grade" />
        <SF label="الفصل" value={cls} onChange={setCls}
            options={[{ value: "__all__", label: "كل الفصول" }, ...classes.map((c) => ({ value: c, label: c }))]}
            disabled={!grade} placeholder={classes.length ? "اختر الفصل" : "لا توجد فصول"} testId="ge-class" />
        <SF label="المادة" value={subjectId} onChange={setSubjectId} options={subjects.map((s) => ({ value: s.id, label: `${s.subject_name} (فصل ${s.semester})` }))} disabled={!cls} testId="ge-subject" />
        <div className="flex items-end gap-2">
          <Button variant="outline" onClick={fillMaxAll} disabled={!subject || students.length === 0}
                  className="h-10" data-testid="ge-fill-max-all" title="تعبئة الحد الأقصى لكل الطلاب دفعة واحدة">
            <CheckCheck className="h-4 w-4 me-1.5" /> تعبئة الكل بالحد الأقصى
          </Button>
          <Button variant="outline" onClick={resetAll} className="h-10" data-testid="ge-reset">
            <RotateCcw className="h-4 w-4 me-1.5" /> تصفير
          </Button>
          <Button onClick={save} disabled={saving || !subject || students.length === 0} className="h-10 flex-1" data-testid="ge-save">
            <Save className="h-4 w-4 me-1.5" /> {saving ? "جارٍ الحفظ…" : "حفظ الكل"}
          </Button>
        </div>
      </div>

      {(!grade || !cls || !subject) ? (
        <div className="rounded-xl border border-dashed border-border bg-white p-16 text-center text-muted-foreground">
          اختر الصف والفصل والمادة لعرض جدول رصد الدرجات.
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/70 border-b border-border">
                <tr>
                  <th className="px-3 py-3 text-start text-[11px] font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap w-16">م.</th>
                  <th className="px-3 py-3 text-start text-[11px] font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap">اسم الطالب</th>
                  <th className="px-3 py-3 text-end text-[11px] font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap w-24">جلوس</th>

                  {mode === "year-work" ? (
                    <>
                      {components.map((c) => (
                        <th key={c.name} className={`px-3 py-3 text-center text-[11px] font-semibold uppercase whitespace-nowrap ${c.is_average ? "text-primary bg-primary/5" : "text-muted-foreground"}`}>
                          <div>{c.icon} {c.name}{c.is_average ? " (متوسط)" : ""}</div>
                          <div className="text-[10px] font-normal text-muted-foreground tabular-nums">(من {c.max_score})</div>
                        </th>
                      ))}
                      <th className="px-3 py-3 text-end text-[11px] font-semibold text-primary uppercase whitespace-nowrap bg-primary/5">المجموع</th>
                    </>
                  ) : (
                    <>
                      <th className="px-3 py-3 text-end text-[11px] font-semibold text-muted-foreground uppercase whitespace-nowrap">
                        مجموع أعمال السنة
                      </th>
                      <th className="px-3 py-3 text-end text-[11px] font-semibold text-primary uppercase whitespace-nowrap bg-primary/5">
                        {isOutcome
                          ? "التقدير (اجتاز/لم يجتز)"
                          : isFormative
                          ? `الاختبار التكويني (من ${subject.max_score})`
                          : `الاختبار النهائي (من ${subject.final_exam_score})`}
                      </th>
                    </>
                  )}
                  <th className="px-3 py-3 text-center text-[11px] font-semibold text-muted-foreground uppercase w-20"> </th>
                </tr>
              </thead>
              <tbody>
                {students.map((st, i) => (
                  <tr key={st.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/40" data-testid={`ge-row-${i}`}>
                    <td className="px-3 py-2 tabular-nums text-muted-foreground">{i + 1}</td>
                    <td className="px-3 py-2 font-medium">{st.name}</td>
                    <td className="px-3 py-2 text-end tabular-nums text-muted-foreground text-xs">{st.seat_number || "—"}</td>

                    {mode === "year-work" ? (
                      <>
                        {components.map((c) => {
                          const row = values[st.id] || {};
                          if (c.is_average) {
                            // متوسط: قيمة مشتقّة تلقائيًا، للعرض فقط — لا
                            // إدخال مباشر، تتحدَّث حيّة مع أي تعديل لمصادرها.
                            return (
                              <td key={c.name} className="px-1.5 py-1.5 text-center" data-testid={`ge-avg-${i}-${c.name}`}>
                                <div className="h-8 flex items-center justify-center rounded-md bg-primary/5 text-primary text-[13px] font-semibold tabular-nums">
                                  {averageValue(row, c).toFixed(2)}
                                </div>
                              </td>
                            );
                          }
                          return (
                            <td key={c.name} className="px-1.5 py-1.5 text-center">
                              <Input
                                type="number" step="0.25" min={0} max={c.max_score}
                                value={row[c.name] ?? ""}
                                onChange={(e) => setYwComp(st.id, c.name, e.target.value === "" ? "" : parseFloat(e.target.value))}
                                onFocus={(e) => e.target.select()}
                                className="h-8 text-center tabular-nums bg-white"
                                data-testid={`ge-input-${i}-${c.name}`}
                              />
                            </td>
                          );
                        })}
                        <td className="px-3 py-2 text-end tabular-nums font-semibold text-primary bg-primary/5">
                          {rowTotal(st.id).toFixed(1)}
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-3 py-2 text-end tabular-nums text-muted-foreground text-xs">
                          {ywTotals[st.id] !== undefined ? Number(ywTotals[st.id]).toFixed(1) : "—"}
                        </td>
                        <td className="px-1.5 py-1.5 text-center bg-primary/5">
                          {isOutcome ? (
                            <Select value={String(values[st.id] || "")} onValueChange={(v) => setFinalVal(st.id, v)}>
                              <SelectTrigger className="h-8" data-testid={`ge-input-outcome-${i}`}>
                                <SelectValue placeholder="—" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="اجتاز">اجتاز</SelectItem>
                                <SelectItem value="لم يجتز">لم يجتز</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input
                              type="number" step="0.25" min={0}
                              max={isFormative ? subject.max_score : subject.final_exam_score}
                              value={values[st.id] ?? ""}
                              onChange={(e) => setFinalVal(st.id, e.target.value === "" ? "" : parseFloat(e.target.value))}
                              onFocus={(e) => e.target.select()}
                              className="h-8 text-center tabular-nums bg-white"
                              data-testid={`ge-input-final-${i}`}
                            />
                          )}
                        </td>
                      </>
                    )}
                    <td className="px-1.5 py-1.5">
                      <Button size="sm" variant="ghost" onClick={() => fillMax(st.id)} title="تعبئة بالحد الأقصى" data-testid={`ge-max-${i}`}>
                        <Zap className="h-4 w-4 text-amber-600" />
                      </Button>
                    </td>
                  </tr>
                ))}
                {students.length === 0 && (
                  <tr><td colSpan={99} className="text-center py-10 text-muted-foreground">لا يوجد طلاب في هذا الفصل.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function SF({ label, value, onChange, options, disabled, placeholder, testId }) {
  const opts = Array.isArray(options) && options.length > 0 && typeof options[0] === "object"
    ? options : options.map((o) => ({ value: o, label: o }));
  return (
    <div className="space-y-1.5">
      <Label className="text-[12px]">{label}</Label>
      <Select value={value || ""} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger className="h-10 bg-white" data-testid={testId}>
          <SelectValue placeholder={placeholder || "اختر"} />
        </SelectTrigger>
        <SelectContent>
          {opts.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
